import { useEffect, useState } from "react";

import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";
import { SearchableOptionList } from "../../common/SearchableOptionList";
import {
  type ChannelWorkspaceRepository,
  discoverRepositoryBranches,
  type SourceControlBranchOption,
} from "../useChannelWorkspaceRepositories";
import { ModalErrorBlock } from "./ConfirmModal";

/**
 * Change the branch of an already-attached repository. The change is recorded
 * immediately; the checkout converges in the background (running env) or when
 * the workspace next starts or resumes (hibernated env). Selecting the
 * currently-applied branch cancels a pending change.
 */
export function ChangeBranchModal({
  busy,
  errorMessage,
  onClose,
  onSubmit,
  repository,
}: {
  busy: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (branch: string) => void;
  repository: ChannelWorkspaceRepository;
}) {
  const appliedBranch =
    repository.defaultBranch ?? repository.repositoryDefaultBranch;
  const [branch, setBranch] = useState(
    repository.pendingBranch ?? appliedBranch ?? "",
  );
  const [manualEntry, setManualEntry] = useState(false);
  const [branches, setBranches] = useState<SourceControlBranchOption[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [branchesError, setBranchesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await discoverRepositoryBranches(
          repository.providerId,
          repository.remoteSlug,
        );
        if (!cancelled) {
          setBranches(list);
          setBranchesLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setBranchesError(
            error instanceof Error ? error.message : "Branch discovery failed",
          );
          setBranchesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repository.providerId, repository.remoteSlug]);

  const branchItems = branches.map((option) => ({
    hint: option.isDefault ? "default" : undefined,
    label: option.name,
    value: option.name,
  }));
  const showList = !manualEntry && !branchesError && branchItems.length > 0;
  const showInput =
    manualEntry ||
    Boolean(branchesError) ||
    (!branchesLoading && branchItems.length === 0);
  const trimmed = branch.trim();
  const cancelsPending =
    repository.pendingBranch != null &&
    appliedBranch != null &&
    trimmed === appliedBranch;
  const canSubmit = trimmed.length > 0 && !busy;

  return (
    <Modal
      title="Change Branch"
      subtitle={`Switch ${repository.remoteSlug} to another branch. The checkout converges in the background — on a hibernated workspace the change applies when it next starts or resumes. Uncommitted work is never discarded.`}
      widthClass="max-w-lg"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            loading={busy}
            loadingText="Recording…"
            onClick={() => onSubmit(trimmed)}
          >
            {cancelsPending ? "Cancel pending change" : "Change branch"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-3">
        {errorMessage && <ModalErrorBlock message={errorMessage} />}
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Current: ⎇ {appliedBranch ?? "default"}
          {repository.pendingBranch != null &&
            ` → ${repository.pendingBranch} (pending)`}
        </p>
        {branchesLoading && (
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Loading branches…
          </p>
        )}
        {showList && (
          <SearchableOptionList
            label="Branch"
            items={branchItems}
            selectedValue={trimmed || null}
            onSelect={setBranch}
            searchPlaceholder="Search branches…"
            emptyMessage="No matching branches"
            maxHeightClassName="max-h-48"
          />
        )}
        {showInput && (
          <label className="grid gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Branch
            <input
              value={branch}
              placeholder={appliedBranch ?? "branch name"}
              onChange={(event) => setBranch(event.currentTarget.value)}
              className="border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </label>
        )}
        {branchesError && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Couldn't list branches — type one manually.
          </p>
        )}
        {showList && (
          <button
            type="button"
            onClick={() => setManualEntry(true)}
            className="cursor-pointer justify-self-start font-mono text-[10px] uppercase tracking-[0.18em] text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-200"
          >
            Type branch manually
          </button>
        )}
        {manualEntry && branchItems.length > 0 && !branchesError && (
          <button
            type="button"
            onClick={() => setManualEntry(false)}
            className="cursor-pointer justify-self-start font-mono text-[10px] uppercase tracking-[0.18em] text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-200"
          >
            Use branch list
          </button>
        )}
      </div>
    </Modal>
  );
}
